import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Shield, AlertTriangle, CheckCircle, Clock, Database, RefreshCw,
  Loader2, BarChart3, Cloud, FileText, Activity, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const scoreColor = (score) => score >= 80 ? 'text-emerald-500' : score >= 50 ? 'text-amber-500' : 'text-red-500';
const scoreBg = (score) => score >= 80 ? 'bg-emerald-500/10' : score >= 50 ? 'bg-amber-500/10' : 'bg-red-500/10';
const statusBadge = (status) => {
  const m = { compliant: 'bg-emerald-500/10 text-emerald-700', at_risk: 'bg-amber-500/10 text-amber-700', non_compliant: 'bg-red-500/10 text-red-700', fail: 'bg-red-500/10 text-red-700', warning: 'bg-amber-500/10 text-amber-700' };
  return m[status] || 'bg-gray-500/10 text-gray-700';
};

export default function ContinuousCompliancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/grc/continuous-compliance');
      setData(res.data);
    } catch (err) {
      console.error('Failed to load compliance data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  const d = data || {};
  const controls = d.controls || {};
  const evidence = d.evidence || {};
  const cspm = d.cspm_scan || {};

  return (
    <div className="p-6 space-y-6" data-testid="continuous-compliance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Continuous Compliance</h1>
          <p className="text-sm text-muted-foreground">Automate evidence collection and monitor controls for audit readiness</p>
        </div>
        <Button onClick={fetchData} variant="ghost" size="icon" data-testid="refresh-compliance-btn"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Audit Readiness Score */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-1">
          <CardContent className="p-5 flex flex-col items-center justify-center h-full">
            <div className={`flex flex-col items-center p-4 rounded-xl ${scoreBg(d.audit_readiness_score || 0)}`} data-testid="audit-readiness-gauge">
              <span className={`text-4xl font-bold ${scoreColor(d.audit_readiness_score || 0)}`}>{d.audit_readiness_score || 0}%</span>
              <span className="text-sm mt-1 text-muted-foreground">Audit Readiness</span>
            </div>
          </CardContent>
        </Card>
        <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card data-testid="stat-controls-implemented">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Controls Implemented</p>
                  <p className="text-2xl font-bold">{controls.implemented || 0}<span className="text-sm text-muted-foreground font-normal">/{controls.total || 0}</span></p>
                  <p className="text-xs text-muted-foreground mt-1">{controls.implementation_rate || 0}% implemented</p>
                </div>
                <div className="p-2 rounded-lg bg-emerald-500/10"><CheckCircle className="w-5 h-5 text-emerald-500" /></div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-evidence-health">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Evidence Health</p>
                  <p className="text-2xl font-bold">{evidence.recent || 0}<span className="text-sm text-muted-foreground font-normal"> recent</span></p>
                  <p className="text-xs text-muted-foreground mt-1">{evidence.stale || 0} stale ({evidence.freshness_rate || 0}% fresh)</p>
                </div>
                <div className="p-2 rounded-lg bg-blue-500/10"><FileText className="w-5 h-5 text-blue-500" /></div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-cspm-deviations">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">CSPM Deviations</p>
                  <p className="text-2xl font-bold text-red-500">{cspm.deviations || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">{cspm.total_findings || 0} total findings</p>
                </div>
                <div className="p-2 rounded-lg bg-red-500/10"><Cloud className="w-5 h-5 text-red-500" /></div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-in-progress">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">In Progress</p>
                  <p className="text-2xl font-bold text-amber-500">{controls.in_progress || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">{controls.not_implemented || 0} not started</p>
                </div>
                <div className="p-2 rounded-lg bg-amber-500/10"><Activity className="w-5 h-5 text-amber-500" /></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Framework Compliance Scores */}
      {d.framework_scores?.length > 0 && (
        <Card data-testid="framework-scores-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Framework Compliance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {d.framework_scores.map((fw) => (
                <div key={fw.id} className="flex items-center gap-4">
                  <div className="w-32 truncate text-sm font-medium">{fw.name || fw.code}</div>
                  <div className="flex-1"><Progress value={fw.score} className="h-3" /></div>
                  <div className="w-14 text-right"><span className={`text-sm font-bold ${scoreColor(fw.score)}`}>{fw.score}%</span></div>
                  <Badge className={`text-xs ${statusBadge(fw.status)}`}>{fw.status?.replace(/_/g, ' ')}</Badge>
                  <div className="text-xs text-muted-foreground w-24 text-right">{fw.implemented}/{fw.total_controls}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* CSPM Deviations */}
        <Card data-testid="cspm-deviations-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Configuration Deviations</CardTitle>
          </CardHeader>
          <CardContent>
            {d.cspm_deviations?.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {d.cspm_deviations.map((dev, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-muted/50 border">
                    <Badge className={`text-xs shrink-0 ${dev.severity === 'critical' ? 'bg-red-500/10 text-red-700' : dev.severity === 'high' ? 'bg-orange-500/10 text-orange-700' : 'bg-amber-500/10 text-amber-700'}`}>{dev.severity}</Badge>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{dev.check_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{dev.provider} — {dev.resource || dev.check_id}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>No configuration deviations detected. {cspm.last_scan ? '' : 'Run an Amidala CSPM scan to check.'}</p>
              </div>
            )}
            {cspm.last_scan && <p className="text-xs text-muted-foreground mt-2">Last CSPM scan: {new Date(cspm.last_scan).toLocaleString()}</p>}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card data-testid="recent-changes-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Recent Control Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {d.recent_changes?.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {d.recent_changes.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-muted/50">
                    <Activity className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="truncate">{c.action?.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-muted-foreground ml-auto shrink-0">{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : ''}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-6 text-muted-foreground text-sm">No recent control activity</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
